/**
 * The display cadence, driven by animation frames with a timer underneath.
 *
 * Two drivers, because each one is blind where the other still works:
 *
 * - `requestAnimationFrame` is aligned to the compositor, so a visible round
 *   clock repaints on the frame it will actually be shown on rather than up to
 *   one timer period late. It stops entirely when the tab is hidden.
 * - `setTimeout` keeps running when frames are paused, throttled to roughly once
 *   a minute in a background tab. That is useless as a *cadence* and exactly
 *   right as a *backstop*: it is what wakes the engine to reconcile when the tab
 *   comes back or when a hidden tab is granted a timer.
 *
 * Neither driver decides how much time passed. Every callback is dispatched by
 * comparing a monotonic reading against a due time, so a frame that arrives late
 * and a timer that arrives an hour late both produce the same thing — one
 * repaint prompt — and the engine reconstructs the elapsed time itself
 * (`docs/02-ARCHITECTURE.md` §4.4). A ticker that fired once per missed boundary
 * would hand a returning tab a burst of repaints and, worse, would tempt a
 * caller into counting them.
 */

import type { Milliseconds } from "@ff/domain";
import type { Cancel, TickerPort } from "@ff/domain/ports";

/**
 * Opaque on purpose. `setTimeout` is typed as returning a `number` in the DOM
 * lib and an object in Node's, and this workspace loads both; naming either one
 * here would make the adapter unbuildable in the other environment.
 */
export type ScheduledHandle = unknown;

export interface TickerEnvironment {
  /** Any non-decreasing millisecond reading. Differences are all that is used. */
  monotonicNow(): Milliseconds;
  setTimer(handler: () => void, delay: Milliseconds): ScheduledHandle;
  clearTimer(handle: ScheduledHandle): void;
  /**
   * Absent where there is no frame loop — a worker, a very old engine, or a
   * hidden tab in a browser that drops the callback rather than deferring it.
   * The ticker keeps working from the timer alone.
   */
  readonly requestFrame?: ((handler: () => void) => ScheduledHandle) | undefined;
  readonly cancelFrame?: ((handle: ScheduledHandle) => void) | undefined;
}

export const TICKER_INTERVAL_RANGE_ERROR = "BrowserTicker.every requires a positive interval";
export const TICKER_DELAY_RANGE_ERROR = "BrowserTicker.after requires a non-negative delay";

export function browserTickerEnvironment(): TickerEnvironment {
  const performanceSource = globalThis.performance as { now?: () => number } | undefined;
  const monotonicNow =
    typeof performanceSource?.now === "function"
      ? performanceSource.now.bind(performanceSource)
      : // A wall-clock cadence is wrong under an NTP correction, but this reading
        // never becomes a duration: the worst outcome is one early or late
        // repaint prompt.
        (): number => Date.now();

  const frames =
    typeof globalThis.requestAnimationFrame === "function"
      ? {
          requestFrame: (handler: () => void): ScheduledHandle =>
            globalThis.requestAnimationFrame(() => {
              handler();
            }),
          cancelFrame: (handle: ScheduledHandle): void => {
            globalThis.cancelAnimationFrame(handle as number);
          },
        }
      : {};

  return {
    monotonicNow,
    setTimer: (handler, delay) => globalThis.setTimeout(handler, delay),
    clearTimer: (handle) => {
      globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>);
    },
    ...frames,
  };
}

interface ScheduledCallback {
  due: Milliseconds;
  /** `undefined` for a one-shot. */
  readonly interval: Milliseconds | undefined;
  readonly callback: () => void;
}

export class BrowserTicker implements TickerPort {
  readonly #environment: TickerEnvironment;
  readonly #scheduled = new Set<ScheduledCallback>();
  #timerHandle: ScheduledHandle | undefined;
  #frameHandle: ScheduledHandle | undefined;

  constructor(environment: TickerEnvironment = browserTickerEnvironment()) {
    this.#environment = environment;
  }

  every(interval: Milliseconds, callback: () => void): Cancel {
    if (!(interval > 0)) {
      // A zero interval would spin the frame loop against a due time it can
      // never overtake, which reads as a hung tab rather than a bad call.
      throw new RangeError(TICKER_INTERVAL_RANGE_ERROR);
    }
    return this.#schedule(interval, interval, callback);
  }

  after(delay: Milliseconds, callback: () => void): Cancel {
    if (!(delay >= 0)) throw new RangeError(TICKER_DELAY_RANGE_ERROR);
    return this.#schedule(delay, undefined, callback);
  }

  /**
   * Drops every pending callback and both drivers. Not part of the port: it is
   * for tearing down a document — or a test — that still has a cadence running.
   */
  dispose(): void {
    this.#scheduled.clear();
    this.#reschedule();
  }

  #schedule(
    delay: Milliseconds,
    interval: Milliseconds | undefined,
    callback: () => void,
  ): Cancel {
    const entry: ScheduledCallback = {
      due: this.#environment.monotonicNow() + Math.round(delay),
      interval: interval === undefined ? undefined : Math.round(interval),
      callback,
    };
    this.#scheduled.add(entry);
    this.#reschedule();

    return () => {
      // Idempotent, and safe to call from inside the callback it cancels:
      // `#pump` re-checks membership before dispatching, so a callback that
      // cancels its own interval is not invoked again in the same pass.
      if (!this.#scheduled.delete(entry)) return;
      this.#reschedule();
    };
  }

  #pump(): void {
    const now = this.#environment.monotonicNow();
    try {
      // A snapshot, because a callback may schedule or cancel: iterating the
      // live set would deliver a tick to something that was just torn down.
      for (const entry of [...this.#scheduled]) {
        if (!this.#scheduled.has(entry) || entry.due > now) continue;

        if (entry.interval === undefined) {
          this.#scheduled.delete(entry);
        } else {
          entry.due += entry.interval;
          // Behind by more than one period — a hidden tab, a busy main thread.
          // The missed prompts are dropped rather than replayed: the screen only
          // has to be right now, and a burst would make the cadence look like a
          // record of what happened.
          if (entry.due <= now) entry.due = now + entry.interval;
        }
        entry.callback();
      }
    } finally {
      // Even a throwing callback must leave the cadence running; the alternative
      // is a live workout whose clock silently stops repainting.
      this.#reschedule();
    }
  }

  #reschedule(): void {
    const environment = this.#environment;

    if (this.#timerHandle !== undefined) {
      environment.clearTimer(this.#timerHandle);
      this.#timerHandle = undefined;
    }

    let earliest: Milliseconds | undefined;
    for (const entry of this.#scheduled) {
      if (earliest === undefined || entry.due < earliest) earliest = entry.due;
    }

    if (earliest === undefined) {
      if (this.#frameHandle !== undefined) {
        environment.cancelFrame?.(this.#frameHandle);
        this.#frameHandle = undefined;
      }
      return;
    }

    const delay = Math.max(0, earliest - environment.monotonicNow());
    this.#timerHandle = environment.setTimer(() => {
      this.#timerHandle = undefined;
      this.#pump();
    }, delay);

    // One frame in flight at a time. Each pump requests the next, so the frame
    // loop sustains itself for as long as anything is scheduled and stops the
    // moment nothing is.
    if (this.#frameHandle === undefined && environment.requestFrame !== undefined) {
      this.#frameHandle = environment.requestFrame(() => {
        this.#frameHandle = undefined;
        this.#pump();
      });
    }
  }
}
